import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import TherapistCard from "@/components/TherapistCard";

const ALL_CITIES = "__all__";
const ALL_SPECIALTIES = "__all__";

const Terapeutas = () => {
  const { t } = useTranslation();
  const [selectedCity, setSelectedCity] = useState<string>(ALL_CITIES);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>(ALL_SPECIALTIES);
  const [defaultCityApplied, setDefaultCityApplied] = useState(false);

  // Los datos publicos SIEMPRE vienen del RPC. La tabla profiles no es accesible sin sesion.
  const { data, isLoading, error } = useQuery({
    queryKey: ["public-therapists"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_therapists");
      if (error) throw error;
      return data ?? [];
    },
  });

  const therapists = useMemo(() => data ?? [], [data]);

  const cities = useMemo(
    () =>
      Array.from(new Set(therapists.map((t) => t.ciudad).filter(Boolean) as string[])).sort(
        (a, b) => a.localeCompare(b, "es")
      ),
    [therapists]
  );

  const specialties = useMemo(
    () =>
      Array.from(
        new Set(therapists.flatMap((th) => (th.tipos_masaje ?? []) as string[]))
      ).sort((a, b) => a.localeCompare(b, "es")),
    [therapists]
  );

  // Ciudad por defecto: Barcelona si existe, una sola vez al llegar los datos.
  useEffect(() => {
    if (defaultCityApplied || therapists.length === 0) return;
    const barcelona = cities.find((c) => c.toLowerCase() === "barcelona");
    if (barcelona) setSelectedCity(barcelona);
    setDefaultCityApplied(true);
  }, [therapists, cities, defaultCityApplied]);

  const labelForSpecialty = (tipo: string) =>
    t(`massageTypes.${tipo}`, {
      defaultValue: tipo.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
    });

  const filtered = useMemo(
    () =>
      therapists.filter((th) => {
        const cityOk = selectedCity === ALL_CITIES || th.ciudad === selectedCity;
        const specialtyOk =
          selectedSpecialty === ALL_SPECIALTIES ||
          ((th.tipos_masaje ?? []) as string[]).includes(selectedSpecialty);
        return cityOk && specialtyOk;
      }),
    [therapists, selectedCity, selectedSpecialty]
  );

  const jsonLd =
    filtered.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: filtered.map((th, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://masshappy.com/terapeutas/${th.id}`,
            name:
              th.es_centro_masajes && th.nombre_centro
                ? th.nombre_centro
                : [th.nombre, th.apellidos].filter(Boolean).join(" "),
          })),
        }
      : undefined;

  const pillClass = (active: boolean) =>
    `rounded-full px-4 py-1.5 text-xs transition-colors ${
      active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={t("therapistDirectory.seoTitle")}
        description={t("therapistDirectory.seoDescription")}
        url="https://masshappy.com/terapeutas"
        jsonLd={jsonLd}
      />

      <main className="container mx-auto max-w-6xl px-4 py-14 md:py-20">
        <header className="text-center">
          <h1 className="font-serif text-3xl md:text-5xl font-semibold leading-tight text-foreground">
            {t("therapistDirectory.title")}
          </h1>
          <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
            {t("therapistDirectory.subtitle")}
          </p>
        </header>

        {cities.length > 0 && (
          <section className="mt-8" aria-labelledby="filtro-ciudad">
            <p id="filtro-ciudad" className="text-center text-xs uppercase tracking-[0.14em] text-muted-foreground">
              {t("therapistDirectory.cityLabel")}
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {[ALL_CITIES, ...cities].map((c) => {
                const active = selectedCity === c;
                return (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setSelectedCity(c)}
                    className={pillClass(active)}
                  >
                    {c === ALL_CITIES ? t("therapistDirectory.all") : c}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {specialties.length > 0 && (
          <section className="mt-6" aria-labelledby="filtro-especialidad">
            <p
              id="filtro-especialidad"
              className="text-center text-xs uppercase tracking-[0.14em] text-muted-foreground"
            >
              {t("therapistDirectory.specialtyLabel")}
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {[ALL_SPECIALTIES, ...specialties].map((s) => {
                const active = selectedSpecialty === s;
                return (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setSelectedSpecialty(s)}
                    className={pillClass(active)}
                  >
                    {s === ALL_SPECIALTIES ? t("therapistDirectory.all") : labelForSpecialty(s)}
                  </button>
                );
              })}
            </div>
          </section>
        )}


        <div className="mt-6">
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-xl border border-border bg-card shadow-soft animate-pulse"
                >
                  <div className="aspect-[4/3] w-full bg-muted" />
                  <div className="space-y-3 p-4">
                    <div className="h-4 w-2/3 rounded bg-muted" />
                    <div className="h-3 w-1/3 rounded bg-muted" />
                    <div className="h-3 w-1/2 rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && error && (
            <p className="text-center text-sm text-muted-foreground">
              {t("therapistDirectory.loadError")}
            </p>
          )}

          {!isLoading && !error && filtered.length === 0 && therapists.length === 0 && (
            <p className="text-center text-sm text-muted-foreground">
              {t("therapistDirectory.emptyAll")}
            </p>
          )}

          {!isLoading && !error && filtered.length === 0 && therapists.length > 0 && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {t("therapistDirectory.emptyFiltered")}
              </p>
              <button
                type="button"
                onClick={() => {
                  setSelectedCity(ALL_CITIES);
                  setSelectedSpecialty(ALL_SPECIALTIES);
                }}
                className="mt-4 rounded-full bg-primary px-5 py-2 text-xs text-primary-foreground transition-opacity hover:opacity-90"
              >
                {t("therapistDirectory.resetFilters")}
              </button>
            </div>
          )}

          {!isLoading && !error && filtered.length > 0 && (
            <>
              <p className="mb-4 text-center text-xs text-muted-foreground">
                {t("therapistDirectory.results", { count: filtered.length })}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((th) => (
                  <Link
                    key={th.id}
                    to={`/terapeutas/${th.id}`}
                    className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-transform hover:-translate-y-0.5"
                  >
                    <TherapistCard therapist={th} />
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terapeutas;
